import { useState, useRef } from 'external/react';
import Icon from 'external/fortawesome';
import { jsonToSheet, bookNew, bookAppendSheet, writeFile } from 'external/xlsx';
import { accountingService } from 'services/accounting.service';
import useAuth from 'context/auth-context/hook';

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'usd' }).format(amount / 100);

const formatDate = (unixTimestamp: number) =>
  new Date(unixTimestamp * 1000).toLocaleDateString();

const statusLabels: Record<string, string> = {
  paid:       'Pagado',
  failed:     'Fallido',
  pending:    'Pendiente',
  in_transit: 'En tránsito',
  canceled:   'Cancelado',
};

const txnTypeLabels: Record<string, string> = {
  charge:         'Cargos',
  refund:         'Reembolsos',
  adjustment:     'Ajustes',
  stripe_fee:     'Comisiones Stripe',
  payout_failure: 'Fallo de pago',
};

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const toDateString = (d: Date) => d.toISOString().split('T')[0];

const PayoutsExportButton = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(toDateString(firstOfMonth));
  const [to, setTo] = useState(toDateString(today));
  const [includeDetail, setIncludeDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const handleExport = async () => {
    setLoading(true);
    setProgress('Obteniendo pagos...');
    try {
      const { data: payouts } = await accountingService.exportPayouts(user.id, {
        from: from || undefined,
        to: to || undefined,
      });

      // Sheet 1: payout summary rows
      const summaryRows = payouts.map((item: any) => ({
        'Pago ID':  item.id,
        'Monto':    formatAmount(item.properties.amount, item.properties.currency),
        'Estado':   statusLabels[item.properties.status] ?? item.properties.status,
        'Depósito': formatDate(item.properties.arrival_date),
        'Banco':    item.bank_name ? `${item.bank_name} •••• ${item.last4}` : '—',
      }));

      const wb = bookNew();
      bookAppendSheet(wb, jsonToSheet(summaryRows), 'Pagos');

      if (includeDetail) {
        const detailRows: any[] = [];

        for (let i = 0; i < payouts.length; i++) {
          const payout = payouts[i];
          setProgress(`Obteniendo detalle ${i + 1} de ${payouts.length}...`);

          const { data: detail } = await accountingService.getPayoutDetail(user.id, payout.id);
          const { transactions, payout: payoutRow } = detail;

          const visibleTxns = transactions.filter(
            (txn: any) => (txn._type ?? txn.type) !== 'payout'
          );

          visibleTxns.forEach((txn: any) => {
            detailRows.push({
              'Pago ID':      payoutRow.id,
              'Depósito':     formatDate(payout.properties.arrival_date),
              'Banco':        payout.bank_name ? `${payout.bank_name} •••• ${payout.last4}` : '—',
              'Tipo':         txnTypeLabels[txn._type ?? txn.type] ?? txn.type,
              'Bruto':        formatAmount(txn.amount, 'usd'),
              'Comisión':     txn.fee === 0 ? '$0.00' : formatAmount(-txn.fee, 'usd'),
              'Total':        formatAmount(txn.net, 'usd'),
              'Descripción':  txn._description ?? txn.description ?? '—',
              'Fecha':        formatDate(txn.created),
            });
          });
        }

        bookAppendSheet(wb, jsonToSheet(detailRows), 'Transacciones');
      }

      setProgress('Generando archivo...');
      writeFile(wb, `pagos_${from || 'inicio'}_${to || 'hoy'}.xlsx`);
      setOpen(false);
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setOpen(o => !o)}
        title="Exportar"
      >
        <Icon name="faHandHoldingDollar" />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', right: 0, top: '110%', zIndex: 100,
            background: 'white', border: '1px solid #dee2e6', borderRadius: 6,
            padding: '12px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: 280,
          }}
        >
          <div className="mb-2">
            <label className="form-label small mb-1">Desde</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label small mb-1">Hasta</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>

          {/* Detail checkbox */}
          <div className="mb-3 d-flex align-items-start gap-2">
            <input
              type="checkbox"
              id="includeDetail"
              className="form-check-input mt-1"
              checked={includeDetail}
              onChange={e => setIncludeDetail(e.target.checked)}
            />
            <label htmlFor="includeDetail" className="form-check-label small" style={{ cursor: 'pointer' }}>
              Incluir detalle de transacciones
              {includeDetail && (
                <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                  Puede tomar más tiempo si los detalles aún no han sido consultados.
                </span>
              )}
            </label>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-primary w-100"
              onClick={handleExport}
              disabled={loading}
            >
              {loading ? (
                <span className="d-flex align-items-center justify-content-center gap-2">
                  <Icon name="faSpinner" spin />
                  <span style={{ fontSize: '0.8rem' }}>{progress}</span>
                </span>
              ) : (
                <><Icon name="faHandHoldingDollar" /> Exportar</>
              )}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              <Icon name="faXmark" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsExportButton;